import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Check, CheckCircle2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import PricingCalculator from "../../components/site/PricingCalculator";
import { PILLARS, BENEFITS, BUSINESS, WHY, FAQ } from "../../components/site/content";
import { useSEO } from "../../lib/api";
import { ConsultantHomeSection } from "./Consultants";

export const Section = ({ id, children, className = "", testid }) => (
  <section id={id} className={`py-16 sm:py-24 ${className}`} data-testid={testid}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
  </section>
);

export const Heading = ({ eyebrow, title, text, light }) => (
  <div className="max-w-2xl mb-12">
    <p className="eyebrow">{eyebrow}</p>
    <h2 className={`mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight ${light ? "text-white" : "text-slate-900"}`}>{title}</h2>
    {text && <p className={`mt-4 text-base md:text-lg leading-relaxed ${light ? "text-slate-300" : "text-slate-600"}`}>{text}</p>}
  </div>
);

const SCREENS = [
  ["ims-manual", "IMS Manual", "Editable IMS / Quality Manual with section approvals and revision history."],
  ["standards", "Standards Library", "ISO standards, clauses and 2015 → 2026 transition mappings you can update yourself."],
  ["audits", "IMS Audit Dashboard", "Audit coverage, findings, corrective actions and asset compliance at a glance."],
  ["ncr", "NCR Register", "Non-conformance register with status tracking and printing."],
  ["jsa", "JSA Register", "Job safety analysis register."],
  ["calibration", "Calibration", "Calibration schedule for measuring equipment."],
  ["objectives", "Objectives & KPIs", "Objectives with KPI targets and actual results."],
  ["quotes", "Quoting", "Quotes register with part history and PDF printing."],
  ["menu-config", "Menu Configuration", "Rename, reorder or hide modules to suit your business."],
];

function Screens() {
  const [tab, setTab] = useState("ims-manual");
  const cur = SCREENS.find((s) => s[0] === tab);
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6" role="tablist">
        {SCREENS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`screens-tab-${k}`}
            className={`px-4 py-2 text-sm font-semibold rounded-md border transition-colors ${tab === k ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{label}</button>
        ))}
      </div>
      <div className="rounded-xl border border-slate-200 bg-slate-100 p-2 shadow-2xl shadow-slate-900/10">
        <div className="flex gap-1.5 px-2 pb-2"><span className="w-2.5 h-2.5 rounded-full bg-slate-300" /><span className="w-2.5 h-2.5 rounded-full bg-slate-300" /><span className="w-2.5 h-2.5 rounded-full bg-slate-300" /></div>
        <img src={`/screens/${cur[0]}.png`} alt={cur[2]} className="w-full rounded-md border border-slate-200 bg-white" data-testid="screens-image" />
      </div>
      <p className="mt-3 text-sm text-slate-500">{cur[2]} Actual screenshot from the current QORDEL 360 application (demonstration account).</p>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#0A192F] text-white" data-testid="home-hero">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 sm:pt-24 sm:pb-28 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <p className="eyebrow !text-sky-400 fade-up">Integrated Management System · Quality first</p>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] fade-up-2">Take Control of Quality, Safety &amp; Compliance.</h1>
          <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-xl fade-up-3">One platform for your ISO 9001, ISO 45001 and ISO 14001 management system — IMS manual, audits, NCRs, risks and registers — with quoting, jobs and production included. Built for businesses and the consultants who support them.</p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 fade-up-3">
            <Link to="/register" className="inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-400 text-white font-semibold px-6 py-3.5 rounded-md transition-colors" data-testid="hero-start-trial-btn">Start Your 5-Day Free Trial <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/features" className="inline-flex items-center justify-center border border-white/25 hover:bg-white/10 text-white font-semibold px-6 py-3.5 rounded-md transition-colors" data-testid="hero-explore-btn">Explore Qordel 360</Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400">
            {["No credit card required", "ISO 9001 · 45001 · 14001 processes", "Australian dollar pricing"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-sky-400" />{t}</span>
            ))}
          </div>
        </div>
        <div className="lg:col-span-6 fade-up-3">
          <div className="rounded-xl bg-white/5 border border-white/10 p-2 backdrop-blur">
            <img src="/screens/ims-manual.png" alt="QORDEL 360 IMS Manual screen" className="w-full rounded-lg" data-testid="hero-screenshot" />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [params] = useSearchParams();
  useEffect(() => {
    const ref = params.get("ref");
    if (ref) localStorage.setItem("q360_ref", ref.toUpperCase().slice(0, 20));
  }, [params]);
  useSEO(null, "QORDEL 360 is integrated management system (IMS) software for Australian businesses. Manage ISO 9001 quality, ISO 45001 safety and ISO 14001 environmental processes, NCRs, audits and risks in one place.");
  return (
    <>
      <Hero />
      <Section testid="home-benefits">
        <Heading eyebrow="Why it matters" title="Less paperwork. More control." text="Bring your management system out of spreadsheets, shared drives and paper folders." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {BENEFITS.map((b) => (
            <div key={b.title} className="lift border border-slate-200 rounded-xl p-6 bg-white">
              <b.icon className="w-6 h-6 text-sky-600" />
              <h3 className="mt-4 text-lg font-semibold text-slate-900">{b.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
      </Section>
      <Section className="bg-slate-50 border-y border-slate-200" testid="home-ims">
        <Heading eyebrow="Integrated Management System" title="Quality, safety and environment — one system." text="Manage the processes associated with ISO 9001, ISO 45001 and ISO 14001 in a single integrated platform." />
        <div className="grid md:grid-cols-3 gap-6">
          {PILLARS.map((p) => (
            <div key={p.title} className="lift bg-white border border-slate-200 rounded-xl p-7" data-testid={`ims-pillar-${p.std.replace(" ", "-").toLowerCase()}`}>
              <div className="flex items-center justify-between"><p.icon className={`w-7 h-7 ${p.color}`} /><span className="font-mono text-xs text-slate-500">{p.std}</span></div>
              <h3 className="mt-5 text-xl font-semibold text-slate-900">{p.title}</h3>
              <ul className="mt-4 space-y-2">
                {p.items.map((i) => <li key={i} className="flex gap-2 text-sm text-slate-600"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />{i}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </Section>
      <Section testid="home-quality">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <img src="/img/factory.jpg" alt="Quality inspection in a manufacturing workshop" className="rounded-xl w-full h-80 lg:h-[420px] object-cover" />
          <div>
            <Heading eyebrow="Quality management" title="Close the loop on every non-conformance." text="Raise an NCR, investigate the root cause, assign corrective actions and verify they worked. Link findings from clause-based internal audits, and review everything in your management review." />
            <Link to="/ims" className="inline-flex items-center gap-2 font-semibold text-sky-700 hover:text-sky-800" data-testid="home-quality-link">See IMS features <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </Section>
      <Section className="bg-slate-50 border-y border-slate-200" testid="home-safety">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="lg:order-2"><img src="/img/engineer.jpg" alt="Engineer wearing safety equipment on site" className="rounded-xl w-full h-80 lg:h-[420px] object-cover" /></div>
          <div>
            <Heading eyebrow="Safety & environmental" title="Hazards, injuries and impacts under control." text="JSAs, plant hazard checks, near misses, the injury register with LTI/MTI graphs, OHS meetings, training matrix, environmental risk, SDS register and legal requirements — all in one system." />
            <Link to="/ims" className="inline-flex items-center gap-2 font-semibold text-sky-700 hover:text-sky-800">Explore safety tools <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </Section>
      <Section testid="home-business">
        <Heading eyebrow="Business tools" title="More Than Compliance — Business Management Tools Included." text="Quoting, invoices, purchase orders, job sheets, shop floor, production, scheduling and inventory are included in your subscription." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-xl overflow-hidden">
          {BUSINESS.map((b) => (
            <div key={b.title} className="bg-white p-6 hover:bg-slate-50 transition-colors">
              <b.icon className="w-5 h-5 text-slate-700" />
              <h3 className="mt-3 font-semibold text-slate-900">{b.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600">{b.text}</p>
            </div>
          ))}
        </div>
      </Section>
      <Section className="bg-slate-50 border-y border-slate-200" testid="home-screenshots">
        <Heading eyebrow="See the product" title="Clean, consistent and easy to use." text="Real screens from the current QORDEL 360 application." />
        <Screens />
      </Section>
      <ConsultantHomeSection />
      <Section id="pricing" testid="home-pricing">
        <Heading eyebrow="Pricing" title="One plan. Every module. Simple per-user pricing." text="Each subscription includes one user. Add more users whenever you need them." />
        <PricingCalculator />
      </Section>
      <Section className="bg-[#0A192F]" testid="home-why">
        <Heading eyebrow="Why QORDEL 360" title="Designed to make your management system easier." light />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {WHY.map((w) => (
            <div key={w.title} className="border border-white/10 rounded-xl p-6 bg-white/[0.03] hover:bg-white/[0.06] transition-colors">
              <h3 className="text-lg font-semibold text-white">{w.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{w.text}</p>
            </div>
          ))}
        </div>
      </Section>
      <Section testid="home-faq">
        <div className="grid lg:grid-cols-3 gap-12">
          <Heading eyebrow="FAQ" title="Frequently asked questions" />
          <FaqList className="lg:col-span-2" />
        </div>
      </Section>
      <TrialCTA />
    </>
  );
}

export function FaqList({ className = "" }) {
  return (
    <Accordion type="single" collapsible className={className} data-testid="faq-accordion">
      {FAQ.map(([q, a], i) => (
        <AccordionItem key={q} value={`f${i}`}>
          <AccordionTrigger className="text-left text-base font-semibold" data-testid={`faq-item-${i}`}>{q}</AccordionTrigger>
          <AccordionContent className="text-slate-600 leading-relaxed">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function TrialCTA() {
  return (
    <section className="bg-sky-600" data-testid="trial-cta">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white">Start your 5-day free trial today.</h2>
          <p className="mt-2 text-sky-100">Full access. One organisation. No credit card required.</p>
        </div>
        <Link to="/register" className="bg-white text-sky-700 hover:bg-sky-50 font-semibold px-6 py-3.5 rounded-md transition-colors" data-testid="cta-start-trial-btn">Start Your 5-Day Free Trial</Link>
      </div>
    </section>
  );
}
